import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { usePrivacy } from "@/context/PrivacyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtCurrency, fmtNumber, fmtDate } from "@/lib/format";
import { ShieldCheck, RefreshCw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { DuplicateCleanup } from "./DuplicateCleanup";

const STATUS = {
    missing: { label: "Missing", cls: "border-loss/40 text-loss bg-loss/5" },
    extra: { label: "Possible duplicates", cls: "border-amber-500/40 text-amber-600 bg-amber-500/5" },
    no_data: { label: "No data", cls: "border-amber-500/40 text-amber-600 bg-amber-500/5" },
    renamed: { label: "Renamed", cls: "border-border text-muted-foreground" },
    none_since_buy: { label: "None yet", cls: "border-border text-muted-foreground" },
    complete: { label: "Complete", cls: "border-gain/40 text-gain bg-gain/5" },
    non_payer: { label: "No dividend", cls: "border-border text-muted-foreground" },
};

const MissingList = ({ items }) => (
    <div className="mt-2 ml-1 space-y-1 text-xs" data-testid="coverage-missing-list">
        {items.map((m) => (
            <div key={`${m.sub_portfolio}-${m.ex_date}`} className="flex flex-wrap items-center gap-x-3 text-muted-foreground num">
                <span className="text-foreground">Ex {fmtDate(m.ex_date)}</span>
                <span>{m.sub_portfolio}</span>
                <span>{fmtNumber(m.shares, 2)} sh × {fmtCurrency(m.amount, { decimals: 4 })}</span>
                <span className="italic">via {m.source}</span>
            </div>
        ))}
    </div>
);

function CoverageRow({ r, onResync, busy }) {
    const { mask } = usePrivacy();
    const [open, setOpen] = useState(false);
    const st = STATUS[r.status] || STATUS.complete;
    return (
        <div className="py-3 border-b border-border/50 last:border-0" data-testid={`coverage-row-${r.ticker}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold num">{r.ticker}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{r.name}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${st.cls}`} data-testid={`coverage-status-${r.ticker}`}>{st.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1" data-testid={`coverage-reason-${r.ticker}`}>{r.reason}</p>
                    {r.missing.length > 0 && (
                        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-primary mt-1 inline-flex items-center gap-1" data-testid={`coverage-toggle-${r.ticker}`}>
                            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Hide" : "Show"} missing payouts
                        </button>
                    )}
                    {open && <MissingList items={r.missing} />}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs num w-full sm:w-auto">
                    <div className="text-right"><div className="text-muted-foreground">Shares</div><div>{fmtNumber(r.shares, 2)}</div></div>
                    <div className="text-right"><div className="text-muted-foreground">Paid while held</div><div data-testid={`coverage-entitled-${r.ticker}`}>{r.entitled}</div></div>
                    <div className="text-right"><div className="text-muted-foreground">Recorded</div><div data-testid={`coverage-recorded-${r.ticker}`}>{r.recorded}{r.recorded_amount ? ` · ${mask(fmtCurrency(r.recorded_amount))}` : ""}</div></div>
                    {r.status !== "renamed" && (
                        <Button size="sm" variant="outline" disabled={busy} onClick={(e) => { e.currentTarget.blur(); onResync(r.ticker); }} className="ml-auto sm:ml-0" data-testid={`coverage-resync-${r.ticker}`}>
                            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Re-sync
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function DividendCoverage({ sub }) {
    const qc = useQueryClient();
    const [busy, setBusy] = useState(null);
    const { data, isLoading } = useQuery({ queryKey: ["div-coverage", sub], queryFn: async () => (await api.get(`/dividends/coverage?sub=${sub}`)).data });

    const resync = async (tk) => {
        setBusy(tk);
        try {
            const { data: res } = await api.post(`/dividends/sync?ticker=${encodeURIComponent(tk)}`);
            if (res.busy) toast.info("A sync is already running — try again in a moment");
            else if (res.added > 0) toast.success(`${tk}: recorded ${res.added} dividend${res.added === 1 ? "" : "s"}`);
            else toast.info(`${tk}: no new dividends found`);
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
    };

    if (isLoading) return <Skeleton className="h-48 rounded-xl" />;
    const rows = data?.rows || [];
    if (!rows.length) return null;
    const s = data.summary || {};
    return (
        <Card className="p-4" data-testid="dividend-coverage-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Dividend Coverage</h2></div>
                {s.extra > 0 && <DuplicateCleanup />}
                <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="coverage-summary">
                    {Object.entries(STATUS).filter(([k]) => s[k]).map(([k, v]) => (
                        <Badge key={k} variant="outline" className={`px-2 py-0 ${v.cls}`}>{s[k]} {v.label}</Badge>
                    ))}
                </div>
            </div>
            <p className="text-sm text-muted-foreground mb-3">For each stock you hold today: how many payouts went ex-dividend while you held it, how many are recorded, and why any are missing.</p>
            <div className="max-h-[520px] overflow-y-auto pr-1">
                {rows.map((r) => <CoverageRow key={r.ticker} r={r} onResync={resync} busy={busy === r.ticker} />)}
            </div>
        </Card>
    );
}
